import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, Check, Sparkles, AlertCircle, ChevronRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const CONDITIONS = [
  { value: "pristine", label: "Pristine — like new" },
  { value: "good", label: "Good — light wear" },
  { value: "fair", label: "Fair — some scratches" },
  { value: "cracked", label: "Already damaged" },
];

const FALLBACK_CATEGORIES = [
  { code: "phone", name: "Phone" },
  { code: "laptop", name: "Laptop" },
  { code: "tablet", name: "Tablet" },
  { code: "smartwatch", name: "Smartwatch" },
  { code: "headphones", name: "Headphones" },
  { code: "camera", name: "Camera" },
  { code: "gaming", name: "Gaming Console" },
  { code: "tv", name: "TV / Monitor" },
  { code: "other", name: "Other" },
];

const CYCLE_LABEL = { monthly: "/ month", quarterly: "/ quarter", annual: "/ year" };

export default function InstantQuote() {
  const nav = useNavigate();
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const [gadgetCategory, setGadgetCategory] = useState("phone");
  const [brand, setBrand] = useState("Apple");
  const [model, setModel] = useState("iPhone 15 Pro");
  const [condition, setCondition] = useState("good");
  const [cycle, setCycle] = useState("annual");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Public masters lookup — falls back to hardcoded list if unauth-blocked.
    api.get("/masters/lookup/gadget_category")
      .then((r) => {
        const rows = (r.data || [])
          .map((row) => ({ code: row.code || row.data?.code, name: row.name || row.data?.name }))
          .filter((c) => c.code);
        if (rows.length) setCategories(rows);
      })
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!brand.trim() || !model.trim()) {
      toast.error("Enter your gadget brand and model");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/quote", { brand, model, condition, cycle });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not fetch a quote");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      id="quote"
      data-testid="instant-quote-widget"
      className="relative bg-card border border-border rounded-3xl p-8 lg:p-10 shadow-teal-glow overflow-hidden"
    >
      <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-secondary/20 blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-2 text-secondary">
          <Zap className="w-4 h-4" />
          <span className="overline">Instant quote — 10 seconds</span>
        </div>
        <h2 className="mt-3 font-display text-3xl lg:text-4xl font-light tracking-tight">
          Get a live price for any gadget.
        </h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-xl">
          No sign-up. No card details. See what cover costs for your phone, laptop, watch — any gadget — right now.
        </p>

        <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Gadget type</label>
            <Select value={gadgetCategory} onValueChange={setGadgetCategory}>
              <SelectTrigger data-testid="quote-gadget-select" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.code} value={c.code} data-testid={`quote-gadget-${c.code}`}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Brand</label>
            <Input
              data-testid="quote-brand-input"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              placeholder="Apple"
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Model</label>
            <Input
              data-testid="quote-model-input"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="iPhone 15 Pro"
              className="mt-1"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Condition</label>
            <Select value={condition} onValueChange={setCondition}>
              <SelectTrigger data-testid="quote-condition-select" className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONDITIONS.map((c) => (
                  <SelectItem key={c.value} value={c.value} data-testid={`quote-condition-${c.value}`}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button
              type="submit"
              disabled={loading}
              data-testid="quote-submit-btn"
              className="w-full bg-primary text-primary-foreground"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Get quote <ChevronRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </div>
        </form>

        {/* Cycle toggle */}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Billing cycle:</span>
          {["monthly", "quarterly", "annual"].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCycle(c)}
              data-testid={`quote-cycle-${c}`}
              className={`px-3 py-1 text-xs rounded-full transition-colors border ${
                cycle === c
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-foreground/70 border-border"
              }`}
            >
              {c[0].toUpperCase() + c.slice(1)}
              {c === "quarterly" && <span className="ml-1 text-secondary font-semibold">−5%</span>}
              {c === "annual" && <span className="ml-1 text-secondary font-semibold">−15%</span>}
            </button>
          ))}
        </div>

        {/* Result */}
        {result && !result.eligible && (
          <div
            data-testid="quote-ineligible"
            className="mt-6 flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-5"
          >
            <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <div className="font-medium text-destructive">{result.reason}</div>
              <div className="text-sm text-muted-foreground mt-1">{result.recommendation}</div>
            </div>
          </div>
        )}

        {result?.eligible && (
          <div className="mt-6" data-testid="quote-result">
            <div className="flex items-center gap-2 text-sm">
              <Check className="w-4 h-4 text-primary" />
              <span className="text-foreground">
                <span className="font-semibold">{result.device.brand} {result.device.model}</span> · {result.device.condition_label} · eligible for cover
              </span>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {result.quotes.map((q) => (
                <div
                  key={q.plan_id}
                  data-testid={`quote-plan-${q.plan_id}`}
                  className={`relative rounded-2xl border p-5 transition-transform hover:-translate-y-0.5 ${
                    q.recommended
                      ? "border-secondary bg-secondary/5 ring-2 ring-secondary shadow-gold-glow"
                      : "border-border bg-background"
                  }`}
                >
                  {q.recommended && (
                    <div className="absolute -top-3 left-4 bg-secondary text-teal text-[10px] overline px-2.5 py-0.5 rounded-full font-bold flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> Recommended
                    </div>
                  )}
                  <div className="overline text-muted-foreground">{q.plan_name}</div>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-3xl font-display font-semibold">£{q.price_gbp.toFixed(2)}</span>
                    <span className="text-xs text-muted-foreground">{CYCLE_LABEL[q.cycle]}</span>
                  </div>
                  <Button
                    size="sm"
                    className={`mt-4 w-full ${q.recommended ? "bg-secondary text-teal hover:bg-secondary/90" : ""}`}
                    onClick={() => nav(`/signup?plan=${q.plan_id}&cycle=${q.cycle}`)}
                    data-testid={`quote-plan-cta-${q.plan_id}`}
                  >
                    Start with {q.plan_name}
                  </Button>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground text-center">
              Prices already include any cycle discount. Cancel anytime.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
